import '../../css/plyr.styles.scss'
import './LessonVideo.scss'

import { useEffect, useState, useCallback, useMemo } from 'react'
import parse from 'html-react-parser'
import Plyr from 'plyr'
import Hls from 'hls.js'
import mux from 'mux-embed'
import cn from 'classnames'

import WebShare from '../web-share'
import { tryGetCourseField } from '../common/api/Courses'
import {
  saveUserEvent,
  EVENT_CATEGORY_VIDEO,
  EVENT_ACTION_ENDED,
  EVENT_ACTION_PAUSE,
  EVENT_ACTION_PLAYING
} from '../common/api/UserEvents'
import { app } from '../../config'
import Bookmark from 'components/bookmark/Bookmark'

// Helper function to get the video title
const _videoTitle = video => {
  // WP Title?
  if (video?.title?.rendered) return parse(video.title.rendered)

  // Custom title string?
  return video.title?.length ? parse(video.title) : ''
}

/**
 * Lesson Video component. Used for displaying a video within a Lesson.
 * TODO: Refactor this to be more a more generic "InlineVideo" component,
 * since this component is used in Lessons and in Practices
 */
export default function LessonVideo({
  video,
  showWebShare = true,
  autoplay = false,
  onVideoEnded = () => {},
  practiceType = null
}) {
  const [playerId, setPlayerId] = useState('player_0')
  const [videoUrl, setVideoUrl] = useState('')
  const [streamUrl, setStreamUrl] = useState('')
  const [captionsUrl, setCaptionsUrl] = useState('')
  const [isPaused, setIsPaused] = useState(true)

  // See https://github.com/sampotts/plyr#the-source-setter
  const options = useMemo(
    () => {
      // Get preview image for video
      const streamId = tryGetCourseField(video, 'mux_stream_id')

      // See https://docs.mux.com/guides/video/get-images-from-a-video#get-an-image-from-a-video for more info
      const poster =
        video.poster ??
        (streamId
          ? `https://image.mux.com/${streamId}/thumbnail.png?width=400&height=225&fit_mode=preserve`
          : '')

      return {
        type: 'video',
        poster,
        fullscreen: {
          enabled: false
        },
        tracks: [
          {
            kind: 'subtitles',
            label: 'Subtitles',
            srclang: 'en',
            src: captionsUrl,
            default: true
          }
        ],
        controls: [
          'play-large',
          'play',
          'progress',
          'current-time',
          'mute',
          'volume',
          'captions',
          'pip' // Picture-in-picture
          // 'airplay' : TODO: Enable this when we fix the background audio issue
        ],
        blankVideo: 'https://media-private.remble.com/blank.mp4',
        mediaMetadata: {
          title: _videoTitle(video),
          artist: 'Remble',
          album: video.course_name ? parse(video.course_name) : '',
          artwork: [
            { src: video.preview ?? '', sizes: '512x512', type: 'image/jpeg' }
          ]
        }
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videoUrl, captionsUrl]
  )

  const handleVideoEvent = useCallback(
    event => {
      const plyr = event.detail.plyr
      setIsPaused(plyr.paused)

      // Hide the video preview header when the video is playing
      document.querySelector('.video-intro-header')?.classList.add('d-none')

      // TODO: mark lesson as complete when video is ended?

      const payload = JSON.stringify({
        videoUrl,
        currentTime: plyr.currentTime
      })

      saveUserEvent(EVENT_CATEGORY_VIDEO, event.type, payload)
    },
    [videoUrl]
  )

  useEffect(() => {
    if (!video) return

    const id = `player_${video.id}`
    const url = tryGetCourseField(video, 'video_url')
    const captions = tryGetCourseField(video, 'captions_url')
    const videoDiv = document.querySelector('.plyr__video-wrapper')

    if (videoDiv) videoDiv.querySelector('video').style.visibility = 'visible'

    setPlayerId(id)
    setVideoUrl(url)
    setCaptionsUrl(captions)
  }, [video])

  const videoRef = useCallback(
    node => {
      // Return early if we don't have a node
      if (!node) return
      try {
        // For more Hls.js options, see https://github.com/dailymotion/hls.js
        const hls = new Hls()

        // Initialize Plyr
        const plyr = new Plyr(node, options)
        const initTime = Date.now()

        // Do we have HLS support and a stream URL?
        if (!Hls.isSupported() || !streamUrl) {
          node.src = videoUrl
        } else {
          hls.loadSource(streamUrl)
          hls.attachMedia(node)
          window.hls = hls

          // Add Mux monitor
          // See https://docs.mux.com/guides/data/monitor-hls-js for more info
          mux.monitor(node, {
            debug: false,
            // pass in the 'hls' instance and the 'Hls' constructor
            hlsjs: hls,
            Hls,
            respectDoNotTrack: false, // Disable tracking of browsers where Do Not Track is enabled
            data: {
              env_key: app.mux.api_key, // required
              // Player Metadata
              player_name: 'Lesson Video Player', // any arbitrary string you want to use to identify this player
              player_init_time: initTime,
              // Video Metadata
              video_id: video.id ?? '', // ex: 'abcd123'
              video_title: _videoTitle(video),
              video_series: video.course_name ? parse(video.course_name) : ''
            }
          })
        }

        // Attach our event listeners
        // See https://github.com/sampotts/plyr#events for more info
        plyr.on(EVENT_ACTION_PLAYING, handleVideoEvent)
        plyr.on(EVENT_ACTION_ENDED, handleVideoEvent)
        plyr.on(EVENT_ACTION_PAUSE, handleVideoEvent)

        // Handle video ended event
        plyr.on(EVENT_ACTION_ENDED, onVideoEnded)
      } catch (error) {}
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [video, videoUrl, streamUrl, options, handleVideoEvent]
  )

  useEffect(() => {
    if (!video) return

    const id = `player_${video.id}`
    const url = tryGetCourseField(video, 'video_url')
    const captions = tryGetCourseField(video, 'captions_url')
    const muxStreamId = tryGetCourseField(video, 'mux_stream_id')

    // Do we have a Mux Stream ID?
    if (muxStreamId) setStreamUrl(`https://stream.mux.com/${muxStreamId}.m3u8`)

    setPlayerId(id)
    setVideoUrl(url)
    setCaptionsUrl(captions)
  }, [video])

  // If we don't have a video or a url, return null
  if (!video || !videoUrl) return null

  return (
    <div className={cn('video-wrapper', { hidePlayer: isPaused })}>
      <Bookmark
        className='position-absolute video-bookmark-button'
        categoryObjectId={video.id}
        category={practiceType}
      />
      {showWebShare && (
        <WebShare
          text={_videoTitle(video)}
          size='fs-5'
          buttonClass='position-absolute video-share-button'
        />
      )}
      <video
        key={playerId}
        id={playerId}
        ref={videoRef}
        playsInline
        crossOrigin='anonymous'
        preload='metadata'
        poster={options.poster}
        autoPlay={autoplay}
      >
        <track
          label='Subtitles'
          kind='subtitles'
          srcLang='en'
          src={captionsUrl}
          default
        />
      </video>
    </div>
  )
}
